<template>
  <template v-if="editionMode">
    <div class="flex flex-col gap-x-1 gap-y-1">
      <div class="fm-form-input mt-2">
        <BaseLabelTop
          :target="name"
          :label="label ?? $t('attributes.TVA')"
          :disabled="disabled"
          :required="required"
        />
        <div class="relative">
          <input
            :id="id"
            v-model="localModelValue"
            :placeholder="placeholder"
            :class="[
              disabled
                ? 'pointer-events-none bg-gray-100 text-gray-500'
                : 'bg-white dark:bg-slate-800',
              sizeClass,
              $attrs.class
            ]"
            class="rounded-md"
          />
          <div
            v-if="checking"
            class="absolute inset-y-0 right-0 mr-2 flex flex-row items-center"
          >
            <span class="size-5 rounded text-center">
              <FontAwesomeIcon
                :icon="['fal', 'spinner-third']"
                class="animate-spin"
              />
            </span>
          </div>
          <template v-else-if="response !== null">
            <div
              class="absolute inset-y-0 right-0 mr-2 flex flex-row items-center"
            >
              <Popover v-if="response.valid" v-slot="{ open }" class="z-100">
                <Float placement="bottom-end" portal :offset="5">
                  <PopoverButton
                    class="grid size-5 place-items-center rounded bg-green-500 text-white"
                    @click="!open && fetchCompanyDetails()"
                  >
                    <FontAwesomeIcon
                      :icon="['fal', 'check']"
                      class="col-start-1 row-start-1 animate-fadeoutfadein"
                    />
                    <FontAwesomeIcon
                      :icon="['fal', 'info-circle']"
                      class="col-start-1 row-start-1 animate-fadeinfadeout"
                    />
                  </PopoverButton>
                  <PopoverPanel
                    class="z-100 min-w-52 overflow-hidden rounded-md border bg-white p-2 shadow-md dark:bg-slate-800"
                  >
                    <div
                      v-if="fetching"
                      class="flex items-center justify-center p-4"
                    >
                      <FontAwesomeIcon
                        :icon="['fal', 'spinner-third']"
                        class="animate-spin"
                      />
                    </div>
                    <template v-else-if="company">
                      <h5 class="font-bold">{{ company.vat }}</h5>
                      <div class="grid grid-cols-[auto_1fr] gap-x-1">
                        <div>
                          <FontAwesomeIcon
                            :icon="['fal', 'building']"
                            size="xs"
                          />
                        </div>
                        <div>
                          <button
                            v-if="company.name"
                            type="button"
                            class="flex w-full items-center gap-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-slate-700"
                            @click="$emit('update:companyName', company.name)"
                          >
                            {{ company.name }}
                          </button>
                        </div>
                        <div>
                          <FontAwesomeIcon
                            :icon="['fal', 'map-marker-alt']"
                            size="xs"
                          />
                        </div>
                        <div>
                          <button
                            v-if="company.address?.street"
                            type="button"
                            class="flex w-full items-center gap-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-slate-700"
                            @click="
                              $emit('update:street', company.address.street)
                            "
                          >
                            {{ company.address.street }}
                          </button>
                          <button
                            v-if="company.address?.zip || company.address?.city"
                            type="button"
                            class="flex w-full items-center gap-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-slate-700"
                            @click="applyZipCity"
                          >
                            {{ company.address.zip }} {{ company.address.city }}
                          </button>
                          <button
                            v-if="company.address?.country"
                            type="button"
                            class="flex w-full items-center gap-2 text-left text-xs hover:bg-gray-50 dark:hover:bg-slate-700"
                            @click="
                              $emit('update:country', company.address.country)
                            "
                          >
                            {{ company.address.country }}
                          </button>
                        </div>
                      </div>
                    </template>
                  </PopoverPanel>
                </Float>
              </Popover>
              <span
                v-else
                class="grid size-5 place-items-center rounded bg-red-600 text-white"
              >
                <FontAwesomeIcon
                  :icon="['fal', 'times']"
                  class="col-start-1 row-start-1 animate-fadeoutfadein"
                />
              </span>
            </div>
          </template>
          <BaseShowEditStatusBadge
            v-else-if="formatStatus === 'invalid'"
            status="invalid"
          />
        </div>
      </div>
      <div
        v-if="response?.company"
        class="flex flex-wrap gap-x-1 px-2 text-xs leading-tight text-slate-500"
      >
        <div class="truncate whitespace-nowrap">
          {{ response.company.name }}
        </div>
        <div class="truncate whitespace-nowrap">
          {{ response.company.address }}
        </div>
      </div>
      <ErrorContainer :error-key="name" :errors="errors" />
    </div>
  </template>

  <template v-else>
    <BaseShowLabel
      :label="label ?? $t('attributes.TVA')"
      :model-value="modelValue"
      :disabled="disabled"
      :class="$attrs.class"
    />
  </template>
</template>

<script>
import { mapActions } from 'vuex'
import BaseShowLabel from '../BaseLabel/BaseShowLabel.vue'
import ErrorContainer from '@c/addf-package/components/BaseShowEditInput/ErrorContainer.vue'
import BaseLabelTop from '@c/addf-package/components/BaseLabel/BaseLabelTop.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/vue'
import { Float } from '@headlessui-float/vue'
import BaseShowEditStatusBadge from '@c/addf-package/components/BaseShowEditInput/BaseShowEditStatusBadge.vue'
import { checkVatFormat, normalizeVat } from '@u/identifiers/companyIdentifiers'

export default {
  name: 'BaseShowEditInputVAT',
  components: {
    Popover,
    PopoverPanel,
    Float,
    PopoverButton,
    FontAwesomeIcon,
    BaseLabelTop,
    BaseShowEditStatusBadge,
    ErrorContainer,
    BaseShowLabel
  },
  props: {
    name: {
      type: String,
      default: ''
    },
    editionMode: {
      type: Boolean,
      default: false
    },
    id: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: null
    },
    /** A valid example, country code included: the format users must type. */
    placeholder: {
      type: String,
      default: 'BE0123456749'
    },
    modelValue: {
      type: String,
      default: null
    },
    errors: {
      type: Array,
      default() {
        return []
      }
    },
    required: {
      type: Boolean,
      required: false,
      default: false
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    size: {
      type: String,
      default: 'medium',
      validator: (v) => ['extra-small', 'small', 'medium', 'large'].includes(v)
    },
    fetchOnMount: {
      type: Boolean,
      default: false,
      required: false
    },
    autoFill: {
      type: Boolean,
      default: false,
      required: false
    },
    overwrite: {
      type: Boolean,
      default: false,
      required: false
    },
    companyName: {
      type: String,
      default: null,
      required: false
    },
    street: {
      type: String,
      default: null,
      required: false
    },
    zip: {
      type: String,
      default: null,
      required: false
    },
    city: {
      type: String,
      default: null,
      required: false
    },
    cityId: {
      type: String,
      default: null,
      required: false
    },
    cityObject: {
      type: Object,
      default: null,
      required: false
    },
    country: {
      type: String,
      default: null,
      required: false
    }
  },
  emits: [
    'update:modelValue',
    'update:companyName',
    'update:street',
    'update:zip',
    'update:city',
    'update:country',
    'update:cityObject',
    'update:cityId'
  ],
  data() {
    return {
      response: null,
      checking: false,
      company: null,
      fetching: false,
      debounceTimer: null
    }
  },
  computed: {
    localModelValue: {
      get() {
        return this.modelValue
      },
      set(val) {
        this.$emit('update:modelValue', val)
      }
    },
    /** Wrong format or missing country code: flagged without asking the back. */
    formatStatus() {
      return checkVatFormat(this.modelValue)
    },
    sizeClass() {
      switch (this.size) {
        case 'extra-small':
          return '!px-2 !py-1 text-xs'
        case 'small':
          return '!px-3 !py-1.5 text-sm'
        case 'large':
          return '!px-5 !py-3'
        case 'medium':
        default:
          return '!px-3 !py-2'
      }
    }
  },
  watch: {
    modelValue(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.response = null
        this.company = null
        clearTimeout(this.debounceTimer)
        if (this.shouldTriggerLookup()) {
          this.debounceTimer = setTimeout(() => this.checkVAT(), 400)
        }
      }
    }
  },
  mounted() {
    if (this.fetchOnMount && this.shouldTriggerLookup()) {
      this.checkVAT()
    }
  },
  beforeUnmount() {
    clearTimeout(this.debounceTimer)
  },
  methods: {
    ...mapActions({
      processCheckVATAction: 'vat/checkVAT',
      processGetCompanyInfoByVatAction: 'vat/getCompanyInfoByVat'
    }),
    checkVAT() {
      this.checking = true
      this.processCheckVATAction({
        vat_number: normalizeVat(this.modelValue)
      })
        .then((response) => {
          this.response = response
          if (this.autoFill && response.valid) {
            this.fetchCompanyDetails()
          }
        })
        .finally(() => (this.checking = false))
    },
    fillProps() {
      if (!this.autoFill || !this.company) return
      const fill = (current, value, event) => {
        if ((this.overwrite || !current) && value) this.$emit(event, value)
      }
      fill(this.companyName, this.company.name, 'update:companyName')
      fill(this.street, this.company.address?.street, 'update:street')
      fill(this.zip, this.company.address?.zip, 'update:zip')
      fill(this.city, this.company.address?.city, 'update:city')
      fill(this.country, this.company.address?.country, 'update:country')
      fill(this.cityId, this.company.address?.city_id, 'update:cityId')
      fill(
        this.cityObject,
        this.company.address?.city_object,
        'update:cityObject'
      )
    },
    shouldTriggerLookup() {
      return this.formatStatus === 'valid'
    },
    applyZipCity() {
      if (this.company.address?.zip)
        this.$emit('update:zip', this.company.address.zip)
      if (this.company.address?.city)
        this.$emit('update:city', this.company.address.city)
      if (this.company.address?.city_id)
        this.$emit('update:cityId', this.company.address.city_id)
      if (this.company.address?.city_object)
        this.$emit('update:cityObject', this.company.address.city_object)
    },
    fetchCompanyDetails() {
      if (this.company) {
        return
      }
      this.fetching = true
      this.processGetCompanyInfoByVatAction({
        vat_number: normalizeVat(this.modelValue)
      })
        .then((response) => {
          this.company = response.company
          this.fillProps()
        })
        .finally(() => (this.fetching = false))
    }
  }
}
</script>
